/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
  applyAppVersionOverride,
  clearAppVersionOverride,
  isDevToolsBuild,
  resolveLaunchAppVersion
} from "../../devtools/appVersionOverride";
import {
  applyMockOverride,
  clearMockOverride,
  isValidMockHostValue,
  readMockOverrideValue
} from "../../devtools/mockOverride";
import { Log } from "../../model/logging";
import {
  isValidAppVersion,
  reloadApplicationAtRoot
} from "../../model/utility";
import type { UnityRootState } from "../UnityAppStore";
import {
  APP_VERSION_OVERRIDE_FOCUS_COUNT,
  AppVersionOverrideFocus
} from "./appVersionOverride.types";
import type { AppVersionOverrideState } from "./appVersionOverride.types";

const TAG: string = "appVersionOverrideSlice";

/**
 * Messages shown in the overlay. Not localised, by design: this is a
 * developer/test tool that never appears in a production build, so adding six
 * locale bundles' worth of strings for it would be misleading rather than
 * helpful.
 */
export const INVALID_VERSION_MESSAGE: string =
  "Invalid version. Use a plain token such as v1.0.561.";
export const INVALID_MOCK_HOST_MESSAGE: string =
  "Invalid mock host. Use <host:port>|<token>, e.g. 192.168.0.2:4500|505c38b651cb.";
export const APPLY_FAILED_MESSAGE: string =
  "Could not store the version override.";
export const MOCK_APPLY_FAILED_MESSAGE: string =
  "Could not store the mock host override.";

const INITIAL_STATE: AppVersionOverrideState = {
  isVisible: false,
  versionInput: "",
  mockHostInput: "",
  activeVersion: "",
  activeMockHost: "",
  versionErrorMessage: "",
  mockHostErrorMessage: "",
  focusedIndex: AppVersionOverrideFocus.VERSION_INPUT
};

/* ── Why every action here ends in a restart ──────────────────────────────
 *
 * Neither override can take effect in the running page. The version decides
 * which *build* is served, and the serving layer reads the `x-app-version`
 * cookie off the request for the next document; the mock host decides which
 * *backend* that build talks to, and `installMockOverride()` snapshots storage
 * at bootstrap while API clients bake their base URL at construction. So each
 * of the four actions below writes, then calls `reloadApplicationAtRoot()` —
 * the app's full-document restart. A router navigation would keep the current
 * build and the current API clients alive and change nothing.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Applies the version currently in the version field.
 *
 * Rejects without touching the cookie when the version fails the shared
 * `isValidAppVersion()` rule, leaving the active version exactly as it was.
 * An empty field is a rejection too, not a silent clear: clearing is what the
 * version Reset button is for, and this way a developer who empties the field
 * by accident is told so rather than being moved to a different build.
 */
export const applyVersionOverride = createAsyncThunk<
  void,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "appVersionOverride/applyVersion",
  async (_: void, { getState, rejectWithValue }) => {
    try {
      const version: string = getState().appVersionOverride.versionInput.trim();

      if (!isValidAppVersion(version)) {
        Log.w(TAG, "applyVersionOverride() — version rejected by validation");
        return rejectWithValue(INVALID_VERSION_MESSAGE);
      }

      if (!applyAppVersionOverride(version)) {
        Log.w(TAG, "applyVersionOverride() — override could not be stored");
        return rejectWithValue(APPLY_FAILED_MESSAGE);
      }

      Log.i(TAG, "applyVersionOverride() — reloading to apply new version");
      reloadApplicationAtRoot();
    } catch (error: unknown) {
      Log.e(TAG, "applyVersionOverride()", error);
      return rejectWithValue(APPLY_FAILED_MESSAGE);
    }
  }
);

/**
 * Removes the version override and restarts, so the reload's document request
 * carries no `x-app-version` and the serving layer falls back to the
 * default/latest build. Leaves the mock host alone.
 */
export const resetVersionOverride = createAsyncThunk<
  void,
  void,
  { state: UnityRootState; rejectValue: string }
>("appVersionOverride/resetVersion", async (_: void, { rejectWithValue }) => {
  try {
    if (!clearAppVersionOverride()) {
      Log.w(TAG, "resetVersionOverride() — override could not be cleared");
      return rejectWithValue(APPLY_FAILED_MESSAGE);
    }
    Log.i(TAG, "resetVersionOverride() — reloading on the default build");
    reloadApplicationAtRoot();
  } catch (error: unknown) {
    Log.e(TAG, "resetVersionOverride()", error);
    return rejectWithValue(APPLY_FAILED_MESSAGE);
  }
});

/**
 * Applies the `<host:port>|<token>` currently in the mock-host field, routing
 * the next launch's API traffic through that mock/proxy server.
 *
 * Validated before anything is written for a reason worth spelling out: an
 * unreachable mock host fails *every* API call, and the only way back is to
 * reopen this overlay, so a value that is obviously malformed is far better
 * caught here than after the restart. Leaves the version alone.
 */
export const applyMockHostOverride = createAsyncThunk<
  void,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "appVersionOverride/applyMockHost",
  async (_: void, { getState, rejectWithValue }) => {
    try {
      const mockHost: string =
        getState().appVersionOverride.mockHostInput.trim();

      if (!isValidMockHostValue(mockHost)) {
        Log.w(TAG, "applyMockHostOverride() — rejected by validation");
        return rejectWithValue(INVALID_MOCK_HOST_MESSAGE);
      }

      if (!applyMockOverride(mockHost)) {
        Log.w(TAG, "applyMockHostOverride() — could not be stored");
        return rejectWithValue(MOCK_APPLY_FAILED_MESSAGE);
      }

      Log.i(TAG, "applyMockHostOverride() — reloading onto the mock host");
      reloadApplicationAtRoot();
    } catch (error: unknown) {
      Log.e(TAG, "applyMockHostOverride()", error);
      return rejectWithValue(MOCK_APPLY_FAILED_MESSAGE);
    }
  }
);

/**
 * Removes the mock-host override and restarts, so the next launch finds
 * nothing stored and talks to the real backend. Leaves the version alone.
 *
 * This is the escape hatch when a mock host has broken every request, which is
 * exactly why it is one button press from the overlay opening rather than an
 * edit-the-field-then-apply sequence.
 */
export const resetMockHostOverride = createAsyncThunk<
  void,
  void,
  { state: UnityRootState; rejectValue: string }
>("appVersionOverride/resetMockHost", async (_: void, { rejectWithValue }) => {
  try {
    if (!clearMockOverride()) {
      Log.w(TAG, "resetMockHostOverride() — could not be cleared");
      return rejectWithValue(MOCK_APPLY_FAILED_MESSAGE);
    }
    Log.i(TAG, "resetMockHostOverride() — reloading on the real backend");
    reloadApplicationAtRoot();
  } catch (error: unknown) {
    Log.e(TAG, "resetMockHostOverride()", error);
    return rejectWithValue(MOCK_APPLY_FAILED_MESSAGE);
  }
});

const appVersionOverrideSlice = createSlice({
  name: "appVersionOverride",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Opens the overlay, seeding each field with what this launch is actually
     * using, so the common case (adjust one of them) needs the fewest
     * keystrokes. A no-op in a release build: the `54321` interceptor is not
     * installed there, so nothing dispatches this, and the guard makes the
     * slice safe on its own regardless of who dispatches it.
     */
    openAppVersionOverride(state): void {
      if (!isDevToolsBuild()) {
        return;
      }
      const activeVersion: string | null = resolveLaunchAppVersion();
      const activeMockHost: string = readMockOverrideValue();
      state.isVisible = true;
      state.activeVersion = activeVersion !== null ? activeVersion : "";
      state.versionInput = activeVersion !== null ? activeVersion : "";
      state.activeMockHost = activeMockHost;
      state.mockHostInput = activeMockHost;
      state.versionErrorMessage = "";
      state.mockHostErrorMessage = "";
      state.focusedIndex = AppVersionOverrideFocus.VERSION_INPUT;
    },

    /** Closes the overlay and discards any unapplied edit. */
    closeAppVersionOverride(state): void {
      state.isVisible = false;
      state.versionInput = "";
      state.mockHostInput = "";
      state.versionErrorMessage = "";
      state.mockHostErrorMessage = "";
      state.focusedIndex = AppVersionOverrideFocus.VERSION_INPUT;
    },

    /**
     * Records a keystroke in the version field. Clears that override's
     * standing error: the message referred to the value the user has now
     * changed. The mock host's message is untouched — it is about a different
     * value that has not changed.
     */
    setVersionInput(state, action: PayloadAction<string>): void {
      state.versionInput = action.payload;
      state.versionErrorMessage = "";
    },

    /** Records a keystroke in the mock-host field, clearing only its error. */
    setMockHostInput(state, action: PayloadAction<string>): void {
      state.mockHostInput = action.payload;
      state.mockHostErrorMessage = "";
    },

    /** Moves focus to the next control, stopping at the last one. */
    focusNextControl(state): void {
      if (state.focusedIndex < APP_VERSION_OVERRIDE_FOCUS_COUNT - 1) {
        state.focusedIndex += 1;
      }
    },

    /** Moves focus to the previous control, stopping at the first one. */
    focusPrevControl(state): void {
      if (state.focusedIndex > 0) {
        state.focusedIndex -= 1;
      }
    },

    /** Sets focus directly — used by pointer hover/focus. */
    setFocusedControl(state, action: PayloadAction<number>): void {
      const index: number = action.payload;
      if (index >= 0 && index < APP_VERSION_OVERRIDE_FOCUS_COUNT) {
        state.focusedIndex = index;
      }
    }
  },
  extraReducers: (builder) => {
    // A rejected apply/reset leaves `isVisible` true and the input untouched:
    // the developer sees why it failed with their value still in the field,
    // and nothing about the running app has moved. Each override reports into
    // its own message, so the error appears under the field that caused it.
    builder.addCase(applyVersionOverride.rejected, (state, action): void => {
      state.versionErrorMessage =
        action.payload !== undefined ? action.payload : APPLY_FAILED_MESSAGE;
    });
    builder.addCase(resetVersionOverride.rejected, (state, action): void => {
      state.versionErrorMessage =
        action.payload !== undefined ? action.payload : APPLY_FAILED_MESSAGE;
    });
    builder.addCase(applyMockHostOverride.rejected, (state, action): void => {
      state.mockHostErrorMessage =
        action.payload !== undefined
          ? action.payload
          : MOCK_APPLY_FAILED_MESSAGE;
    });
    builder.addCase(resetMockHostOverride.rejected, (state, action): void => {
      state.mockHostErrorMessage =
        action.payload !== undefined
          ? action.payload
          : MOCK_APPLY_FAILED_MESSAGE;
    });
  }
});

export const {
  openAppVersionOverride,
  closeAppVersionOverride,
  setVersionInput,
  setMockHostInput,
  focusNextControl,
  focusPrevControl,
  setFocusedControl
} = appVersionOverrideSlice.actions;

export default appVersionOverrideSlice.reducer;

/* ── Selectors ────────────────────────────────────────────────────────── */

export const selectIsAppVersionOverrideVisible = (
  state: UnityRootState
): boolean => state.appVersionOverride.isVisible;

export const selectAppVersionInput = (state: UnityRootState): string =>
  state.appVersionOverride.versionInput;

export const selectMockHostInput = (state: UnityRootState): string =>
  state.appVersionOverride.mockHostInput;

export const selectActiveAppVersion = (state: UnityRootState): string =>
  state.appVersionOverride.activeVersion;

export const selectActiveMockHost = (state: UnityRootState): string =>
  state.appVersionOverride.activeMockHost;

export const selectAppVersionOverrideError = (state: UnityRootState): string =>
  state.appVersionOverride.versionErrorMessage;

export const selectMockHostOverrideError = (state: UnityRootState): string =>
  state.appVersionOverride.mockHostErrorMessage;

export const selectAppVersionOverrideFocusedIndex = (
  state: UnityRootState
): number => state.appVersionOverride.focusedIndex;
